<template>
<div class="filelayout-vue">
    <div v-if="local_layouts.length > 0">
        <h3>File Column Layouts</h3>
        <p>File layouts allow you to define how your data should be formatted when exporting files for direct mail or other purposes. You can specify column names, data sources, and modifications to apply to the data.</p>

        <div class="card mb-3 table-card">
            <div class="card-header">
                <div class="row">
                    <div class="col-lg-6 col-12">
                        <h3 class="mt-2">File Layouts ({{filtered_layouts.length}})</h3>
                    </div>
                    <div class="col-lg-6 d-none d-lg-block">
                        <div class="d-flex justify-content-end justify-self-end gap-2">
                            <div class="position-relative">
                                <input type="text" class="search-box form-control me-1 py-2" placeholder="Search by Layout Name..." aria-label="Search by Layout Name..." v-model="search_key" @input="handleSearch()">
                                <i class="fa fa-search search-placeholder-icon" v-if="search_key === ''"></i>
                            </div>
                            <a :href="'/direct_mail/'+ client.url +'/file_layouts/new'" class="btn btn-primary" v-if="canEdit('direct_mail')">
                                <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="">Add New File Layout
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-body p-0">
                <table class="table table-responsive table-with-pagination m-0">
                    <thead>
                        <tr class="table-header-row">
                            <th @click="sortByKey('name')" class="table-column-1">
                                <div class="d-flex flex-row">
                                    Name
                                    <div class="d-flex flex-column">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'name' && sort_order === 'asc'}" alt="">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'name' && sort_order === 'des'}" alt="">
                                    </div>
                                </div>
                            </th>
                            <th @click="sortByKey('columns')" class="table-column-3">
                                <div class="d-flex flex-row">
                                    Num. Columns
                                    <div class="d-flex flex-column">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'columns' && sort_order === 'asc'}" alt="">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'columns' && sort_order === 'des'}" alt="">
                                    </div>
                                </div>
                            </th>
                            <th @click="sortByKey('created_at')" class="table-column-3">
                                <div class="d-flex flex-row">
                                    Date Created
                                    <div class="d-flex flex-column">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'created_at' && sort_order === 'asc'}" alt="">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'created_at' && sort_order === 'des'}" alt="">
                                    </div>
                                </div>
                            </th>
                            <th class="table-column-3 pe-3"  width="1" v-if="canEdit('direct_mail')">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(l, index) in list_shown" :key="l.id" :class="`table-row-${index+1}`">
                            <td class="align-middle"><a :href="'/direct_mail/'+ client.url +'/file_layouts/edit/' + l.id">{{ l.name }}</a></td>
                            <td class="align-middle">{{ number(l.columns.length) }}</td>
                            <td class="align-middle">
                                <span :title="datetime(l.created_at)" data-bs-toggle="tooltip">
                                    {{ date(l.created_at) }}
                                </span>
                            </td>
                            <td class="align-middle" v-if="canEdit('direct_mail')">
                                <div class="dropdown d-flex justify-content-center">
                                    <button class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false">
                                        <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="">
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                        <a class="dropdown-item" :href="'/direct_mail/'+ client.url +'/file_layouts/edit/' + l.id">
                                            <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit Layout
                                        </a>
                                        <a class="dropdown-item" :href="'/direct_mail/' + client.url +'/file_layouts/clone/' + l.id">
                                            <img class="icon me-2" src="/img/icons/avid-icons/document-copy.svg" alt=""> Copy Layout
                                        </a>
                                        <hr class="my-1">
                                        <a class="dropdown-item text-danger" href="#" @click.prevent="removeLayout(l.id)">
                                            <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete Layout
                                        </a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot class="table-border-bottom-0">
                        <tr>
                            <td colspan="100%" class="py-0">
                                <div class="table-pagination">
                                    <div class="table-pagination--group d-none d-md-flex">
                                        <div class="position-relative">
                                            <select class="form-select" name="num_per_page" id="num_per_page" v-model="num_per_page" @change="reloadPagination(filtered_layouts)" aria-label="Num Per Page">
                                                <option value="5000">ALL</option>
                                                <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                            </select>
                                            <div class="select-display-text">
                                                Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="table-pagination--group d-none d-lg-flex">
                                        {{ filtered_layouts.length === 0 ? '0' : item_interval }} of {{ filtered_layouts.length }}
                                    </div>

                                    <div class="table-pagination--group">
                                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage(filtered_layouts)" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                                            <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                                        </button>

                                        <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                                            <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="{'current-page': page === page_index + 1}" @click="moveToPage(page, filtered_layouts)">
                                                {{ page }}
                                            </button>
                                        </div>
                                        <div class="d-flex page-selector" v-else>
                                            <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="{'current-page': page === page_index + 1}" @click="moveToPage(page, filtered_layouts)">
                                                {{ page }}
                                            </button>

                                            <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                            ...
                                            </button>
                                            <button v-if="page_index >= 2 && page_index < page_index_array.length - 1" class="btn btn-none btn-icon-only-md current-page">
                                                {{ page_index + 1 }}
                                            </button>

                                            <button v-if="page_index < page_index_array.length - 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                            ...
                                            </button>
                                            <button class="btn btn-none btn-icon-only-md " :class="{'current-page': page_index === page_index_array.length - 1}" @click="moveToPage(page_index_array.length, filtered_layouts)" :key="page_index_array.length">
                                                {{ page_index_array.length }}
                                            </button>
                                        </div>

                                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage(filtered_layouts)" :disabled="page_index === page_count - 1" data-bs-toggle="tooltip" title="Next page">
                                            <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>

    <div v-if="!local_layouts.length">
        <h4>No file layouts have been created yet.</h4>
        <p>
            Creating a file layout allows you to define how your data should be formatted when exporting files for direct mail or other purposes. You can specify column names, data sources, and modifications to apply to the data.
        </p>
        <a :href="'/direct_mail/'+ client.url +'/file_layouts/new'" class="btn btn-primary mb-3" v-if="canEdit('direct_mail')">
            <span><i class="fas fa-fw fa-plus"></i>Add New File Layout</span>
        </a>
    </div>
</div>
</template>

<script>
import { defineComponent } from 'vue';
import tablePaginationMixin from '../../mixins/table_pagination';
import Swal from 'sweetalert2';
import permissionsMixin from '../../mixins/permissions'

export default defineComponent({
    name: 'FileLayouts',
    props: {
        client: {
            type: Object,
            required: true
        },
        layouts: {
            type: Array,
            required: true
        }
    },
    mixins: [tablePaginationMixin, permissionsMixin],
    data() {
        return {
            local_layouts: this.layouts,
            filtered_layouts: [],
            list_shown: [],
            search_key: '',
            sort_key: 'name',
            sort_order: 'asc'
        };
    },
    beforeMount() {
        this.filtered_layouts = [...this.layouts];
        this.reloadPagination(this.filtered_layouts);
    },
    mounted() {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

        for (var i = 0; i < tooltipTriggerList.length; i++) {
            (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                    tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                    tooltipList[index].hide();
                });
            })(i);
        }
    },
    methods: {
        removeLayout(layout_id) {
            var _self = this;

            //Show a warning
            Swal.fire({
                title: "Are you sure you want to delete this file layout?",
                text: "This layout will no longer be available for file exports.",
                icon: "error",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    //Make a call to the api to set active = 0 in the DB
                    axios.delete('/api/file_layout/' + layout_id)
                    .then(response => {
                        //Remove it from my local array too
                        var index = this.filtered_layouts.findIndex(layout => layout.id === layout_id);
                        this.filtered_layouts.splice(index, 1);
                        this.reloadPagination(this.filtered_layouts);

                        Swal.fire({
                            title: "Deleted!",
                            text: "The file layout has been removed.",
                            icon: "success",
                            customClass: {
                                popup: 'swal-success'
                            },
                        });
                    });
                }
            });
        },
        handleSearch() {
            this.page_index = 0;
            this.reloadPagination(this.local_layouts);
        },
        sortByKey(key) {
            if (this.sort_key === key) {
                this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
            } else {
                this.sort_key = key;
                this.sort_order = 'asc';
            }

            if (key === 'columns') {
                if (this.sort_order === 'asc') {
                    this.filtered_layouts.sort((a, b) => a.columns.length - b.columns.length);
                } else {
                    this.filtered_layouts.sort((a, b) => b.columns.length - a.columns.length);
                }
            } else {
                if (this.sort_order === 'asc') {
                    this.filtered_layouts.sort((a, b) => (a[key] > b[key]) ? 1 : -1);
                } else {
                    this.filtered_layouts.sort((a, b) => (a[key] < b[key]) ? 1 : -1);
                }
            }

            this.reloadPagination(this.filtered_layouts);
        }
    }
});
</script>
